import React, { useState } from 'react'
import { 
  Clock, FileText, MessageSquare, Bell, Euro, 
  CheckCircle, XCircle, Send, Upload,
  User, Settings, FileCheck, Briefcase, Pencil, Loader2, Trash2, X, Check
} from 'lucide-react'
import { toast } from 'sonner'
import { RichNoteEditor, NoteContent } from './RichNote'
import { useAuth } from '../../contexts/AuthContext'

const API_URL = process.env.REACT_APP_BACKEND_URL

const EVENT_CONFIG = {
  changement_statut: { 
    icon: Settings, 
    color: 'bg-blue-100 text-blue-600',
    borderColor: 'border-blue-200'
  },
  commentaire: { 
    icon: MessageSquare, 
    color: 'bg-slate-100 text-slate-600',
    borderColor: 'border-slate-200'
  },
  document_ajout: { 
    icon: Upload, 
    color: 'bg-emerald-100 text-emerald-600',
    borderColor: 'border-emerald-200'
  },
  relance_envoyee: { 
    icon: Bell, 
    color: 'bg-amber-100 text-amber-600',
    borderColor: 'border-amber-200'
  },
  paiement: { 
    icon: Euro, 
    color: 'bg-green-100 text-green-600',
    borderColor: 'border-green-200'
  },
  provision_mise_a_jour: { 
    icon: Euro, 
    color: 'bg-orange-100 text-orange-600',
    borderColor: 'border-orange-200'
  },
  tache_creee: { 
    icon: CheckCircle, 
    color: 'bg-purple-100 text-purple-600',
    borderColor: 'border-purple-200'
  },
  email_recu: { 
    icon: Send, 
    color: 'bg-cyan-100 text-cyan-600',
    borderColor: 'border-cyan-200'
  },
  email_envoye: { 
    icon: Send, 
    color: 'bg-indigo-100 text-indigo-600',
    borderColor: 'border-indigo-200'
  },
  note_interne: { 
    icon: FileText, 
    color: 'bg-yellow-100 text-yellow-600',
    borderColor: 'border-yellow-200'
  },
  expertise: { 
    icon: Briefcase, 
    color: 'bg-violet-100 text-violet-600',
    borderColor: 'border-violet-200'
  },
  offre_compagnie: { 
    icon: FileCheck, 
    color: 'bg-teal-100 text-teal-600',
    borderColor: 'border-teal-200'
  },
}

const STATUT_LABELS = {
  declare: 'Declare',
  en_instruction: 'En instruction',
  en_attente_compagnie: 'Attente compagnie',
  expertise_en_cours: 'Expertise en cours',
  negociation: 'Negociation',
  cloture_favorable: 'Cloture favorablement',
  cloture_refus: 'Cloture - Refus',
  sans_suite: 'Sans suite',
}

const NOTE_COLORS = [
  { key: '', label: 'Defaut', bg: 'bg-white', border: 'border-slate-200', dot: 'bg-slate-300' },
  { key: 'jaune', label: 'Jaune', bg: 'bg-yellow-50', border: 'border-yellow-300', dot: 'bg-yellow-400' },
  { key: 'orange', label: 'Orange', bg: 'bg-orange-50', border: 'border-orange-300', dot: 'bg-orange-400' },
  { key: 'rose', label: 'Rose', bg: 'bg-pink-50', border: 'border-pink-300', dot: 'bg-pink-400' },
  { key: 'rouge', label: 'Rouge', bg: 'bg-red-50', border: 'border-red-300', dot: 'bg-red-400' },
  { key: 'violet', label: 'Violet', bg: 'bg-purple-50', border: 'border-purple-300', dot: 'bg-purple-400' },
  { key: 'bleu', label: 'Bleu', bg: 'bg-blue-50', border: 'border-blue-300', dot: 'bg-blue-400' },
  { key: 'vert', label: 'Vert', bg: 'bg-emerald-50', border: 'border-emerald-300', dot: 'bg-emerald-400' },
  { key: 'gris', label: 'Gris', bg: 'bg-slate-100', border: 'border-slate-300', dot: 'bg-slate-400' },
]

function getColorStyle(couleur) {
  const found = NOTE_COLORS.find(c => c.key === (couleur || ''))
  return found || NOTE_COLORS[0]
}

export default function SinistreTimeline({ events, onRefresh }) {
  if (!events || events.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock size={32} className="mx-auto text-slate-300 mb-2" />
        <p className="text-sm text-slate-400">Aucun evenement enregistre</p>
      </div>
    )
  }

  function formatDate(dateStr) {
    const date = new Date(dateStr)
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  function formatStatusChange(event) {
    if (event.type_evenement !== 'changement_statut') return null
    const newStatus = event.nouvelle_valeur?.statut
    const oldStatus = event.ancienne_valeur?.statut
    
    if (newStatus && !oldStatus) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
          {STATUT_LABELS[newStatus] || newStatus}
        </span>
      )
    }
    
    if (newStatus && oldStatus) {
      return (
        <span className="text-xs">
          <span className="text-slate-400 line-through">{STATUT_LABELS[oldStatus] || oldStatus}</span>
          {' → '}
          <span className="font-medium text-blue-700">{STATUT_LABELS[newStatus] || newStatus}</span>
        </span>
      )
    }
    
    return null
  }

  // Group events by date
  const groupedEvents = events.reduce((acc, event) => {
    const dateKey = formatDate(event.created_at)
    if (!acc[dateKey]) acc[dateKey] = []
    acc[dateKey].push(event)
    return acc
  }, {})

  return (
    <div className="space-y-6" data-testid="sinistre-timeline">
      {Object.entries(groupedEvents).map(([date, dayEvents]) => (
        <div key={date}>
          {/* Date header */}
          <div className="flex items-center gap-3 mb-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              {date}
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Events */}
          <div className="space-y-2">
            {dayEvents.map((event) => {
              const isNote = event.type_evenement === 'note_interne' || event.type_evenement === 'commentaire'
              
              if (isNote) {
                return <NoteCard key={event.id} event={event} formatDate={formatDate} onRefresh={onRefresh} />
              }

              // Standard event rendering (non-notes)
              const config = EVENT_CONFIG[event.type_evenement] || EVENT_CONFIG.commentaire
              const Icon = config.icon

              return (
                <div 
                  key={event.id}
                  className={`flex gap-3 p-3 rounded-lg border ${config.borderColor} bg-white`}
                  data-testid={`event-${event.id}`}
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${config.color}`}>
                    <Icon size={13} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{event.titre}</p>
                        {event.contenu && (
                          <NoteContent contenu={event.contenu} className="text-sm text-slate-600 mt-0.5" />
                        )}
                        {formatStatusChange(event)}
                      </div>
                      <span className="text-[10px] text-slate-400 shrink-0">
                        {formatDate(event.created_at)}
                      </span>
                    </div>
                    {event.user_profiles && (
                      <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-slate-400">
                        <User size={10} />
                        <span>{event.user_profiles.prenom} {event.user_profiles.nom}</span>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Editable Note Card with color picker ──
function NoteCard({ event, formatDate, onRefresh }) {
  const { profile } = useAuth()
  const canDelete = profile?.role === 'master_admin'
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(event.contenu || '')
  const [saving, setSaving] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)

  // Color stored in nouvelle_valeur.couleur
  const eventCouleur = event.nouvelle_valeur?.couleur || ''
  const colorStyle = getColorStyle(eventCouleur)

  async function handleSaveEdit() {
    if (editText === event.contenu) { setEditing(false); return }
    setSaving(true)
    try {
      const r = await fetch(`${API_URL}/api/sinistres/evenements/${event.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contenu: editText }),
      })
      if (!r.ok) throw new Error('Erreur')
      toast.success('Note modifiee')
      setEditing(false)
      onRefresh?.()
    } catch {
      toast.error('Erreur de sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  async function handleColorChange(colorKey) {
    setShowColorPicker(false)
    try {
      const r = await fetch(`${API_URL}/api/sinistres/evenements/${event.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ couleur: colorKey }),
      })
      if (!r.ok) throw new Error('Erreur')
      onRefresh?.()
    } catch {
      toast.error('Erreur couleur')
    }
  }

  async function handleDelete() {
    if (!window.confirm('Supprimer cette note ?')) return
    try {
      const r = await fetch(`${API_URL}/api/sinistres/evenements/${event.id}?user_id=${profile?.id || ''}`, { method: 'DELETE' })
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Erreur')
      toast.success('Note supprimee')
      onRefresh?.()
    } catch (e) {
      toast.error(e.message || 'Erreur suppression')
    }
  }

  return (
    <div
      className={`relative rounded-lg border p-3 ${colorStyle.bg} ${colorStyle.border} group`}
      data-testid={`note-${event.id}`}
    >
      {/* Date only — no "note interne", no icon, no hour */}
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-medium text-slate-500">
          {formatDate(event.created_at)}
        </span>
        {/* Action buttons — visible on hover */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="p-1 rounded hover:bg-black/5"
            title="Couleur"
            data-testid={`note-color-btn-${event.id}`}
          >
            <div className={`w-3.5 h-3.5 rounded-full border border-slate-300 ${colorStyle.dot}`} />
          </button>
          <button
            onClick={() => { setEditText(event.contenu || ''); setEditing(true) }}
            className="p-1 rounded hover:bg-black/5 text-slate-400 hover:text-indigo-600"
            title="Modifier"
            data-testid={`note-edit-btn-${event.id}`}
          >
            <Pencil size={12} />
          </button>
          {canDelete && (
            <button
              onClick={handleDelete}
              className="p-1 rounded hover:bg-black/5 text-slate-400 hover:text-red-500"
              title="Supprimer (master admin)"
              data-testid={`note-delete-btn-${event.id}`}
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Color picker dropdown */}
      {showColorPicker && (
        <div className="absolute right-2 top-8 z-20 bg-white rounded-lg shadow-lg border border-slate-200 p-2 flex flex-wrap gap-1.5 w-40"
          data-testid="note-color-picker">
          {NOTE_COLORS.map(c => (
            <button
              key={c.key}
              onClick={() => handleColorChange(c.key)}
              className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${c.dot} ${
                eventCouleur === c.key ? 'ring-2 ring-offset-1 ring-indigo-400' : 'border-transparent'
              }`}
              title={c.label}
              data-testid={`note-color-${c.key || 'default'}`}
            />
          ))}
        </div>
      )}

      {/* Content */}
      {editing ? (
        <div>
          <RichNoteEditor value={editText} onChange={setEditText} autoFocus minRows={3} testId={`note-edit-textarea-${event.id}`} />
          <div className="flex justify-end gap-1.5 mt-1.5">
            <button onClick={() => setEditing(false)} className="px-2 py-1 text-xs text-slate-400 hover:text-slate-600">
              <X size={12} />
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={saving}
              className="px-3 py-1 bg-indigo-600 text-white rounded text-xs flex items-center gap-1"
              data-testid={`note-save-btn-${event.id}`}
            >
              {saving ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
              Enregistrer
            </button>
          </div>
        </div>
      ) : (
        event.contenu
          ? <NoteContent contenu={event.contenu} className="text-sm text-slate-700 leading-relaxed" />
          : <p className="text-sm text-slate-400 italic">Note vide</p>
      )}

      {/* Author — compact */}
      {event.user_profiles && (
        <div className="flex items-center gap-1 mt-1.5 text-[10px] text-slate-400">
          <User size={9} />
          <span>{event.user_profiles.prenom} {event.user_profiles.nom}</span>
        </div>
      )}
    </div>
  )
}
